import { useState, useEffect } from "react";
import { Gift, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { BrutalButton } from "@/components/Brutal";
import { GroupMultiSelect } from "@/components/Selects";
import api from "@/lib/api";
import { toast } from "sonner";

export default function NewGiftPotDialog({ open, onClose, users = [], meId, onCreated, prefill }) {
  const [title, setTitle] = useState("");
  const [recipient, setRecipient] = useState("");
  const [date, setDate] = useState("");
  const [goal, setGoal] = useState("");
  const [link, setLink] = useState("");
  const [groupIds, setGroupIds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);

  const reset = () => { setTitle(""); setRecipient(""); setDate(""); setGoal(""); setLink(""); setGroupIds([]); };

  useEffect(() => {
    if (open) {
      api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {});
      if (prefill) {
        setTitle(prefill.title || "");
        setRecipient(prefill.recipient_id || "");
        setDate(prefill.event_date || "");
      }
    }
    // eslint-disable-next-line
  }, [open]);

  const create = async () => {
    if (!title.trim()) return toast.error("Geef de cadeaupot een titel");
    setBusy(true);
    try {
      const { data } = await api.post("/giftpots", {
        title: title.trim(), recipient_id: recipient || null,
        event_date: date || null, goal: Number(goal) || 0, payment_link: link.trim() || null,
        group_ids: groupIds,
      });
      toast.success("Cadeaupot aangemaakt 🎁");
      reset(); onClose(); onCreated && onCreated(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-w-md max-h-[90vh] overflow-auto no-scrollbar" data-testid="new-giftpot-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Gift className="w-5 h-5" strokeWidth={2.5} />Nieuwe cadeaupot</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-brand-ink/60">Waarvoor is de pot? *</label>
            <input data-testid="new-pot-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="bv. Afscheidscadeau Jan, nieuwe koffiemachine" className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none bg-brand-cream" />
          </div>
          <div>
            <label className="text-xs font-bold text-brand-ink/60">Voor wie? (optioneel)</label>
            <select data-testid="new-pot-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none bg-brand-cream">
              <option value="">Voor het hele huis</option>
              {users.filter((u) => u.user_id !== meId).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
            <p className="text-[11px] text-brand-ink/50 font-semibold mt-1">Kies je een persoon? Dan blijft de pot voor hem/haar verborgen. 🤫</p>
          </div>
          <div>
            <label className="text-xs font-bold text-brand-ink/60">Zichtbaar voor</label>
            <div className="mt-1"><GroupMultiSelect groups={groups} value={groupIds} onChange={setGroupIds} testid="new-pot-groups" /></div>
            <p className="text-[11px] text-brand-ink/50 font-semibold mt-1">"Iedereen" = het hele huis. Kies een groep om de pot alleen daar te tonen.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-brand-ink/60">Datum (optioneel)</label>
              <DatePicker testid="new-pot-date" value={date} onChange={(v) => setDate(v)} className="bg-brand-cream px-3 py-2.5" />
            </div>
            <div>
              <label className="text-xs font-bold text-brand-ink/60">Streefbedrag €</label>
              <input data-testid="new-pot-goal" type="number" min="0" step="1" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="0" className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none bg-brand-cream" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-brand-ink/60">Betaal-link / IBAN (optioneel)</label>
            <input data-testid="new-pot-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://tikkie.me/… of IBAN NL.." className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none bg-brand-cream" />
            <p className="text-[11px] text-brand-ink/50 font-semibold mt-1">💡 Tip: kies bij Tikkie de optie <b>"vrij te kiezen bedrag"</b>, zodat iedereen zelf zijn bijdrage bepaalt.</p>
          </div>
          <BrutalButton data-testid="new-pot-create" onClick={create} disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" strokeWidth={2.5} />}Pot aanmaken
          </BrutalButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
